import React, { useEffect, useRef, useState } from "react";
import { IoChevronDown } from "react-icons/io5";
import useAccount from "../../../../hooks/useAccount";
import ManageOrderPaymentModal from "./modals/ManageOrderPaymentModal";
import { manageOrderHoldFlightBooking } from "./manageOrderControllers";
import ManageOrderRebookModal from "./modals/ManageOrderRebookModal";
import CancelOrder from "../../../Orders/cancelOrder";
import { set } from "lodash";
import ManageOrderIssueTicketModal from "./modals/ManageOrderIssueTicketModal";
import ManageOrderCancelTicketRequest from "./modals/ManageOrderCancelTicketRequest";
import ManageOrderManualIssueModal from "./modals/ManageOrderManualIssueModal";
import ManageOrderCancelTicketModal from "./modals/ManageOrderCancelTicketModal";
import ManageOrderApproveTicketRequest from "./modals/ManageOrderApproveTicketRequest";
import ManageOrderMoreInfoTicketRequestModal from "./modals/ManageOrderMoreInfoTicketRequestModal";

function BookingConfirmationManageOrder({ order }) {
  const { checkPermission } = useAccount();
  const isTicket = Boolean(order?.flightBooking);
  const flightBooking = order
    ? order?.flightBooking || order?.booking?.flightBooking?.at(0)
    : {};

  const [manageMenu, setManageMenu] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const [modal, setModal] = useState({
    payment: false,
    rebook: false,
    cancel: false,
    issue: false,
    denyTicket: false,
    manualIssue: false,
    cancelTicket: false,
    approve: false,
    moreInfo: false,
  });
  const [loading, setLoading] = useState({
    hold: false,
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        manageMenu &&
        !menuRef.current?.contains(event.target) &&
        !buttonRef.current?.contains(event.target)
      ) {
        setManageMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [manageMenu]);

  const data = isTicket ? order : flightBooking;

  const menuItems = [
    {
      label: "Make Payment",
      visible:
        ["Booked", "Not Paid"].includes(data?.status) &&
        !isTicket &&
        checkPermission("pay:ticket"),
      action: () => {
        setModal((prev) => ({ ...prev, payment: true }));
      },
    },
    {
      label: "Issue Ticket",
      visible:
        ["Paid", "issuable", "PendingTicketIssue", "Pending"].includes(
          data?.status,
        ) && checkPermission("issue:ticket"),
      action: () => {
        setModal((prev) => ({ ...prev, issue: true }));
      },
    },
    {
      label: "Manual Issue",
      visible: ["Pending"].includes(data?.status) && isTicket,
      action: () => {
        setModal((prev) => ({ ...prev, manualIssue: true }));
      },
    },
    {
      label: "Hold Booking",
      visible:
        ["Booked", "Not Paid"].includes(data?.status) &&
        !isTicket &&
        checkPermission("hold:flight-booking"),
      action: () => {
        manageOrderHoldFlightBooking(
          order?.booking || data,
          loading,
          setLoading,
        );
      },
    },
    {
      label: "Rebook",
      visible:
        ["Booked", "Not Paid", "Paid"].includes(data?.status) && !isTicket,
      action: () => {
        setModal((prev) => ({ ...prev, rebook: true }));
      },
    },
    {
      label: "Cancel order",
      visible:
        ["Booked", "Not Paid", "Paid"].includes(data?.status) &&
        !isTicket &&
        checkPermission("cancel:flight-booking"),
      action: () => {
        setModal((prev) => ({ ...prev, cancel: true }));
      },
    },
    {
      label: "Approve",
      visible: ["Open"].includes(data?.status) && isTicket,
      action: () => {
        setModal((prev) => ({ ...prev, approve: true }));
      },
    },
    {
      label: "Request more info",
      visible: ["Open"].includes(data?.status) && isTicket,
      action: () => {
        setModal((prev) => ({ ...prev, moreInfo: true }));
      },
    },
    {
      label: "Cancel Request",
      visible:
        ["PendingTicketIssue", "Pending"].includes(data?.status) && isTicket,
      action: () => {
        setModal((prev) => ({ ...prev, denyTicket: true }));
      },
    },
    {
      label: "Cancel Ticket",
      visible:
        ["Ticket issued", "issued", "Issued", "Completed"].includes(
          data?.status,
        ) && isTicket,
      action: () => {
        setModal((prev) => ({ ...prev, cancelTicket: true }));
      },
    },
  ].filter((i) => i.visible);

  if (!menuItems.length) return null;

  const loadingInController = loading.hold;

  return (
    <div className="relative w-full lg:w-fit">
      <ManageOrderMoreInfoTicketRequestModal
        modal={modal}
        setModal={setModal}
        booking={data}
      />
      <ManageOrderApproveTicketRequest
        modal={modal}
        setModal={setModal}
        booking={data}
      />
      <ManageOrderManualIssueModal
        modal={modal}
        setModal={setModal}
        booking={{ ...order, flightBooking: [order?.flightBooking] }}
      />
      <ManageOrderCancelTicketModal
        modal={modal}
        setModal={setModal}
        booking={flightBooking}
      />
      <ManageOrderPaymentModal
        modal={modal}
        setModal={setModal}
        booking={flightBooking}
      />
      <ManageOrderRebookModal
        modal={modal}
        setModal={setModal}
        booking={flightBooking}
      />
      <ManageOrderIssueTicketModal
        modal={modal}
        setModal={setModal}
        booking={flightBooking}
      />
      <CancelOrder
        open={modal.cancel ? flightBooking?._id : false}
        setOpen={(modalState) =>
          setModal((prev) => ({ ...prev, cancel: modalState }))
        }
        callback={() => {
          setTimeout(() => {
            window.location.reload();
          }, 300);
        }}
      />
      <ManageOrderCancelTicketRequest
        modal={modal}
        setModal={setModal}
        booking={order}
      />

      <button
        ref={buttonRef}
        disabled={loadingInController}
        onClick={() => setManageMenu((prev) => !prev)}
        className="border border-gray-300 h-12 rounded-md bg-gray-100 hover:bg-gray-200 transition-all w-full px-2 lg:px-4 flex items-center justify-center gap-2 text-sm font-semibold"
      >
        {loadingInController ? (
          <div className="primaryLoader"></div>
        ) : (
          <>
            <span>Manage order</span>
            <IoChevronDown
              className={`text-base transition-transform mt-[2px] ${
                manageMenu ? "rotate-180" : ""
              }`}
            />
          </>
        )}
      </button>

      {manageMenu && (
        <div
          ref={menuRef}
          className="absolute right-0 mt-2 w-44 rounded-md border border-gray-200 bg-white shadow-lg overflow-hidden z-20"
        >
          {menuItems.map((item, idx) => (
            <button
              key={idx}
              onClick={() => {
                setManageMenu(false);
                item.action && item.action();
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                item.className || ""
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default BookingConfirmationManageOrder;
